import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    Chip,
    CircularProgress,
    Skeleton,
    TextField,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    getVoiceExerciseModels,
    getSuggestedModels,
    createVoiceAssignment,
} from "../../../api/services/api";
import { toast } from "react-toastify";

export function AiModelAssignment() {
    const { id: patientProfileId } = useParams();
    const [searchParams] = useSearchParams();
    const diagnosisCode = searchParams.get("diagnosis") || "";
    const sessionId = searchParams.get("sessionId");
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [selectedModels, setSelectedModels] = useState<number[]>([]);
    const [schedule, setSchedule] = useState("DAILY");
    const [notes, setNotes] = useState("");

    const { data: modelsData, isLoading: modelsLoading } = useQuery(
        ["voiceExerciseModels"],
        getVoiceExerciseModels
    );

    const { data: suggestedData } = useQuery(
        ["suggestedModels", diagnosisCode],
        getSuggestedModels,
        { enabled: !!diagnosisCode }
    );

    const models = modelsData?.data || [];
    const suggestedModels = suggestedData?.data || [];
    const suggestedIds = suggestedModels.map((m: any) => m.model_id || m.id);

    const assignMutation = useMutation(createVoiceAssignment, {
        onSuccess: () => {
            toast.success("AI model assigned successfully");
            queryClient.invalidateQueries(["patientAssignments"]);
            navigate(-1);
        },
        onError: () => {
            toast.error("Failed to assign model");
        },
    });

    const toggleModel = (id: number) => {
        setSelectedModels((prev) =>
            prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
        );
    };

    const handleSubmit = () => {
        if (selectedModels.length === 0) {
            toast.warn("Please select at least one AI model");
            return;
        }
        assignMutation.mutate({
            patientProfileId: parseInt(patientProfileId || "0"),
            modelIds: selectedModels,
            sessionId: sessionId ? parseInt(sessionId) : undefined,
            schedule,
            notes: notes || undefined,
        });
    };

    return (
        <Box sx={{ p: 3, maxWidth: 700, mx: "auto" }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{ mb: 2, textTransform: "none", color: "#64748b" }}
            >
                Back
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Assign AI Voice Exercise Model
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mb: 3 }}>
                Select one or more AI models to assign to this patient. The system will
                generate daily exercises based on the selected models and schedule.
            </Typography>

            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                    You must approve each model assignment. The AI will suggest models
                    based on the diagnosis, but you make the final decision on what the
                    patient receives.
                </Typography>
            </Alert>

            {/* Suggested Models */}
            {suggestedModels.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Suggested for this diagnosis
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        {suggestedModels.map((m: any) => {
                            const model = models.find(
                                (mod: any) => mod.id === (m.model_id || m.id)
                            ) || m;
                            const isSelected = selectedModels.includes(model.id);
                            return (
                                <Card
                                    key={model.id}
                                    onClick={() => toggleModel(model.id)}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        cursor: "pointer",
                                        border: isSelected
                                            ? "2px solid #3d7169"
                                            : "1px solid #e2e8f0",
                                        bgcolor: isSelected ? "rgba(61,113,105,0.04)" : "#fff",
                                        transition: "all 0.2s",
                                        "&:hover": { borderColor: "#3d7169" },
                                        flex: "1 1 200px",
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                                        {isSelected ? (
                                            <CheckCircleOutlineIcon sx={{ color: "#3d7169" }} />
                                        ) : (
                                            <SmartToyOutlinedIcon sx={{ color: "#94a3b8" }} />
                                        )}
                                        <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                            {model.display_name || model.name}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                                        {model.description || ""}
                                    </Typography>
                                    <Chip
                                        label="Suggested"
                                        size="small"
                                        sx={{
                                            mt: 1,
                                            bgcolor: "#eff6ff",
                                            color: "#2563eb",
                                            fontWeight: 600,
                                            fontSize: "0.65rem",
                                        }}
                                    />
                                </Card>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* All Models */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                All Available Models
            </Typography>
            {modelsLoading ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} variant="rectangular" width={200} height={100} sx={{ borderRadius: 2 }} />
                    ))}
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
                    {models
                        .filter((m: any) => !suggestedIds.includes(m.id))
                        .map((model: any) => {
                            const isSelected = selectedModels.includes(model.id);
                            return (
                                <Card
                                    key={model.id}
                                    onClick={() => toggleModel(model.id)}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        cursor: "pointer",
                                        border: isSelected
                                            ? "2px solid #3d7169"
                                            : "1px solid #e2e8f0",
                                        bgcolor: isSelected ? "rgba(61,113,105,0.04)" : "#fff",
                                        transition: "all 0.2s",
                                        "&:hover": { borderColor: "#3d7169" },
                                        flex: "1 1 200px",
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                                        {isSelected ? (
                                            <CheckCircleOutlineIcon sx={{ color: "#3d7169" }} />
                                        ) : (
                                            <SmartToyOutlinedIcon sx={{ color: "#94a3b8" }} />
                                        )}
                                        <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                            {model.display_name || model.name}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                                        {model.description || ""}
                                    </Typography>
                                    <Chip
                                        label={model.category || "CLINICAL"}
                                        size="small"
                                        sx={{
                                            mt: 1,
                                            bgcolor: model.category === "WELLNESS" ? "#f0fdf4" : "#f8fafc",
                                            color: model.category === "WELLNESS" ? "#166534" : "#64748b",
                                            fontWeight: 600,
                                            fontSize: "0.65rem",
                                        }}
                                    />
                                </Card>
                            );
                        })}
                </Box>
            )}

            {/* Schedule */}
            <FormControl fullWidth sx={{ mb: 3 }}>
                <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Exercise Schedule</FormLabel>
                <Select
                    value={schedule}
                    onChange={(e) => setSchedule(e.target.value)}
                    size="small"
                    sx={{ borderRadius: 2 }}
                >
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="ALTERNATE_DAYS">Alternate Days</MenuItem>
                    <MenuItem value="THREE_PER_WEEK">3 Times per Week</MenuItem>
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                </Select>
            </FormControl>

            {/* Notes */}
            <TextField
                label="Notes (optional)"
                placeholder="Any specific instructions for this patient's exercises..."
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                sx={{ mb: 3 }}
            />

            {/* Submit */}
            <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={selectedModels.length === 0 || assignMutation.isLoading}
                fullWidth
                sx={{
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: "#3d7169",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                    "&:hover": { bgcolor: "#254137" },
                }}
            >
                {assignMutation.isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                    `Assign ${selectedModels.length} Model${selectedModels.length !== 1 ? "s" : ""}`
                )}
            </Button>
        </Box>
    );
}

export default AiModelAssignment;
